import React, { useEffect, useState } from 'react';

const C = {
  bg:     '#0d1117',
  card:   '#161b22',
  border: '#21262d',
  text:   '#e6edf3',
  muted:  '#8b949e',
  blue:   '#4a9eff',
  green:  '#36d399',
  orange: '#f6c90e',
  roed:   '#f87171',
};

const fmt = (v, suffix = ' kr') => {
  if (v === null || v === undefined) return '—';
  if (v >= 1000000) return (v / 1000000).toFixed(2) + 'M' + suffix;
  if (v >= 1000)    return Math.round(v / 1000) + 'k' + suffix;
  return Math.round(v) + suffix;
};

function HalvmaaneGauge({ pct, label, status }) {
  const r     = 70;
  const cx    = 90;
  const cy    = 90;
  const angle = Math.min(pct, 200) * 1.8 * (Math.PI / 180); // 0 → PI (halvcirkel)
  const clampedAngle = Math.min(angle, Math.PI);
  const endX  = cx - r * Math.cos(clampedAngle);
  const endY  = cy - r * Math.sin(clampedAngle);
  const large = clampedAngle > Math.PI / 2 ? 1 : 0;

  const barColor = status === 'ok' ? C.green : status === 'advarsel' ? C.orange : C.roed;

  return (
    <div style={{ textAlign: 'center' }}>
      <svg width="180" height="100" viewBox="0 0 180 100" style={{ maxWidth: '100%' }}>
        {/* Baggrund */}
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke={C.border}
          strokeWidth="14"
          strokeLinecap="round"
        />
        {/* Fyld */}
        {pct > 0 && (
          <path
            d={`M ${cx - r} ${cy} A ${r} ${r} 0 ${large} 1 ${endX} ${endY}`}
            fill="none"
            stroke={barColor}
            strokeWidth="14"
            strokeLinecap="round"
          />
        )}
        {/* Tick ved 100% (midtpunkt) */}
        <line x1={cx} y1={cy - r - 6} x2={cx} y2={cy - r + 6} stroke={C.muted} strokeWidth="1.5" />
        <text x={cx} y={cy - r - 10} fill={C.muted} fontSize="10" textAnchor="middle">100%</text>
        {/* Vaerdi */}
        <text x={cx} y={cy - 12} fill={barColor} fontSize="22" fontWeight="bold" textAnchor="middle">
          {pct.toFixed(0)}%
        </text>
        <text x={cx} y={cy + 8} fill={C.muted} fontSize="11" textAnchor="middle">{label}</text>
      </svg>
    </div>
  );
}

function KPIKort({ label, vaerdi, underTekst, farve, stor }) {
  return (
    <div style={{
      background:   C.card,
      border:       `1px solid ${C.border}`,
      borderRadius: 12,
      padding:      '16px 20px',
      flex:         1,
      minWidth:     140,
    }}>
      <div style={{ color: C.muted, fontSize: 12, marginBottom: 6 }}>{label}</div>
      <div style={{ color: farve || C.text, fontSize: stor ? 28 : 22, fontWeight: 800 }}>{vaerdi}</div>
      {underTekst && <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>{underTekst}</div>}
    </div>
  );
}

export default function FinansielSundhedSR() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [fejl, setFejl]       = useState(null);

  useEffect(() => {
    fetch('/api/os/strategisk/finansielt-sundhed', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.fejl) setFejl(d.fejl); else setData(d); })
      .catch(e => setFejl(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 32, color: C.muted, textAlign: 'center' }}>Henter finansdata...</div>;
  if (fejl)    return <div style={{ padding: 32, color: C.roed }}>Fejl: {fejl}</div>;
  if (!data)   return null;

  const statusFarve = data.status === 'ok' ? C.green : data.status === 'advarsel' ? C.orange : C.roed;
  const runwayFarve = (data.runway_maaneder || 0) > 6 ? C.green : (data.runway_maaneder || 0) > 3 ? C.orange : C.roed;

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '20px 16px', color: C.text, fontFamily: 'system-ui, sans-serif' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>Finansiel Sundhed</h2>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>MRR, loenforpligtelse og runway</div>

      {/* 3 KPI-kort */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <KPIKort
          label="MRR (seneste)"
          vaerdi={fmt(data.mrr)}
          underTekst="Monthly Recurring Revenue"
          farve={C.blue}
          stor
        />
        <KPIKort
          label="Loenforpligtelse-daekning"
          vaerdi={`${data.daekning_pct?.toFixed(0)}%`}
          underTekst={`Maal: ${fmt(data.loen_forpligtelse)}/md daekket`}
          farve={statusFarve}
          stor
        />
        <KPIKort
          label="Runway (estimat)"
          vaerdi={data.runway_maaneder ? `${data.runway_maaneder} mdr` : '—'}
          underTekst="ved nuvaerende MRR / fast burn"
          farve={runwayFarve}
          stor
        />
      </div>

      {/* Halvmaane gauge */}
      <div style={{
        background:   C.card,
        border:       `1px solid ${C.border}`,
        borderRadius: 12,
        padding:      '20px 16px',
        marginBottom: 20,
        display:      'flex',
        flexWrap:     'wrap',
        alignItems:   'center',
        gap:          24,
      }}>
        <HalvmaaneGauge
          pct={data.daekning_pct || 0}
          label="Loenforpligtelse"
          status={data.status}
        />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>Status</div>
          <div style={{
            display:      'inline-block',
            padding:      '4px 12px',
            borderRadius: 20,
            background:   `${statusFarve}22`,
            color:        statusFarve,
            fontSize:     13,
            fontWeight:   700,
            textTransform: 'uppercase',
            letterSpacing: 1,
          }}>
            {data.status === 'ok' ? 'SUND' : data.status === 'advarsel' ? 'ADVARSEL' : 'KRITISK'}
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
            MRR dækker {data.daekning_pct?.toFixed(0)}% af loenforpligtelsen<br />
            ({fmt(data.mrr)} vs. {fmt(data.loen_forpligtelse)})
          </div>
        </div>
      </div>

      {/* Trend */}
      {data.trend && data.trend.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px', overflowX: 'auto' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>MRR Trend (seneste maaneder)</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {data.trend.map(t => (
              <div key={t.maaned} style={{ textAlign: 'center', minWidth: 80 }}>
                <div style={{ color: C.blue, fontSize: 16, fontWeight: 700 }}>{fmt(t.mrr)}</div>
                <div style={{ color: C.muted, fontSize: 11 }}>{t.maaned}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}